import type { ReconnectStrategy } from './types';
import type { EventMessage } from '../emitter';
import { createEmitter } from '../emitter';
import { reconnectStrategy as defaultReconnectStrategy } from './reconnect-strategy';

type UrlSetting = string | (() => string);

type Settings = {
  pingInterval?: number;
  reconnectStrategy?: ReconnectStrategy;
};

type Events = {
  open: Event;
  message: MessageEvent;
  close: CloseEvent;
  error: Event;
  fail: CloseEvent | Event;
};

export function createWebsocketClient(url: UrlSetting, { reconnectStrategy = defaultReconnectStrategy(), pingInterval }: Settings = {}) {
  const getUrl = () => {
    if (typeof url === 'function') {
      return url();
    }

    return url;
  };

  function closeSocket(): void {
    if (socket && socket.readyState < WebSocket.CLOSING) {
      socket.close(1000);
    }
  }
  function close() {
    if (closed) {
      console.warn(
        `[WebsocketClient] ${new Date().toLocaleString()} - Socket already closed`
      );
      return client;
    }
    closed = true;
    emitter.all.clear();
    closeSocket();
    self.removeEventListener('offline', closeSocket);
    self.removeEventListener('online', handleOnline);

    return client;
  }
  function ping() {
    if (pingTimeout) {
      clearTimeout(pingTimeout);
    }

    if (!pingInterval) {
      return undefined;
    }

    pingTimeout = setTimeout(() => {
      if (navigator.onLine && socket?.readyState === WebSocket.OPEN) {
        socket.send('ping');
        ping();
      }
    }, pingInterval);
  }
  async function runReconnectStrategy(event: CloseEvent | Event) {
    reconnectCount++;
    const result = await reconnectStrategy({
      reconnectCount,
      event
    });
    if (!result) {
      emitter.emit('fail', event);
      console.error(`[WebsocketClient] ${new Date().toLocaleString()} - WS reconnect failure`);
      close();
      return;
    }
    reconnect();
  }

  function reconnect() {
    if (!navigator.onLine || (socket && socket.readyState < WebSocket.CLOSING)) {
      return;
    }
    console.warn(
      `[WebsocketClient] ${new Date().toLocaleString()} - reconnect ${reconnectCount}`
    );
    socket = createSocket();
  }
  function createSocket(): WebSocket {
    const newSocket = new WebSocket(getUrl());
    const onOpen = (event: Event) => {
      console.log(`[WebsocketClient] ${new Date().toLocaleString()} - open`);
      if (closed) {
        closeSocket();
        return;
      }
      reconnectCount = 0;
      emitter.emit('open', event);
      ping();
    };
    newSocket.addEventListener('open', onOpen);
    const onMessage = (event: MessageEvent) => {
      emitter.emit('message', event);
    };
    newSocket.addEventListener('message', onMessage);
    const onError = (event: Event) => {
      emitter.emit('error', event);
    };
    newSocket.addEventListener('error', onError);
    const onClose = (event: CloseEvent) => {
      emitter.emit('close', event);
      closeSocket();
      newSocket.removeEventListener('open', onOpen);
      newSocket.removeEventListener('message', onMessage);
      newSocket.removeEventListener('error', onError);
      newSocket.removeEventListener('close', onClose);
      if (closed) {
        console.log(`[WebsocketClient] ${new Date().toLocaleString()} - close`);
        return;
      }
      runReconnectStrategy(event);
    };
    newSocket.addEventListener('close', onClose);
    return newSocket;
  }
  function handleOnline(event: Event) {
    console.log(
      `[WebsocketClient] ${new Date().toLocaleString()} - You are now connected to the network.`
    );
    reconnectCount = 0;
    runReconnectStrategy(event);
  }
  function on<K extends keyof Events>(type: K, listener: (message: EventMessage<K, Events[K]>) => void) {
    return emitter.on(type, listener as any);
  }

  const emitter = createEmitter<Events>();
  let reconnectCount = 0;
  let pingTimeout: ReturnType<typeof setTimeout> | undefined;
  let closed = false;
  let socket: WebSocket | undefined;

  const client = {
    on,
    open: () => {
      if (closed) {
        throw new Error(`[WebsocketClient] ${new Date().toLocaleString()} - Socket will be closed`);
      }

      socket = createSocket();
      // В Chrome и Firefox соединение ведёт себя по-разному в случае потери сети:
      // в Firefox - ошибка, Chrome - продолжает висеть в открытом статусе.
      // Для приведения к одному поведению сделана обработка событий offline/online
      self.addEventListener('offline', closeSocket);
      self.addEventListener('online', handleOnline);

      return client;
    },
    close
  };

  return client;
}
